import { AppSelect } from '../../ui/AppSelect';
import { ModelArkReferenceLibraryPanel } from '../../assets/ModelArkLibraryControls';
import { isDomesticOfficialSeedance } from '../../../utils/domesticSeedanceCapabilities';
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, Send, Pencil, ChevronDown, Settings2, RotateCcw, AlertCircle, Share2, Palette, X, History, Wand2 } from "lucide-react";
import { CANVAS_VIDEO_MODELS as VIDEO_MODELS } from "../utils/videoModels";
import { getSeedance25Capabilities, normalizeSeedance25Resolution, normalizeSeedance25SubmissionProvider } from "../../../utils/seedance25Capabilities";
import { applyLighting } from "../../storyboard/common/LightingPresets";
import {
  getProjectDefaultVideoRatio,
  getProjectVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoResolutionForProvider,
} from "../../../utils/projectRuntime";
import { IMAGE_QUALITY_TIER_OPTIONS } from "../../../constants/imageQualityTiers";
import { getCompatibleImageAspectOptions, normalizeDatastartImageAspectRatio } from '../../../constants/datastartImageModels';
import { normalizeImageProvider } from "../../../constants/globalAiProviders";
import GenerateSettingsModal from "./GenerateSettingsModal";
import GenerationHistoryStrip from "./GenerationHistoryStrip";
import EstimatedPoints from "../../common/EstimatedPoints";
import ImageProviderSelect from "../../common/ImageProviderSelect";
import {
  getCurrentGenerationHistoryEntry,
  normalizeGenerationHistory,
  restoreGenerationHistoryParameters,
  selectGenerationHistoryVersion,
} from "../utils/generationHistory";
import {
  IMAGE_PROVIDER_CHANGE_EVENT,
  getCurrentImageProvider,
  getWanxCompatibleQualityOptions,
  normalizeWanxImageQualityTier,
} from "../../../utils/wanxImageCapabilities";
import {
  isWanxVideoProvider,
  normalizeWanxVideoModel,
  normalizeWanxVideoRatio,
  normalizeWanxVideoResolution,
  WANX_VIDEO_MODEL,
} from "../../../utils/wanxVideoCapabilities";

const VIDEO_PROVIDERS = VIDEO_MODELS.map(m => ({ id: m.id, name: m.name, color: m.color || m.accentColor || 'var(--color-accent-primary)' }));
const getProviderConfig = (providerId) => VIDEO_MODELS.find(m => m.id === providerId);
const normalizeCanvasVideoResolution = (provider, resolution) => (
  isWanxVideoProvider(provider)
    ? normalizeWanxVideoResolution(resolution)
    : getSeedance25Capabilities(provider)
      ? normalizeSeedance25Resolution(provider, resolution)
      : normalizeVideoResolutionForProvider(provider, resolution, "720p")
);

const ASPECT_RATIOS = ["16:9", "21:9", "9:16", "1:1", "4:3", "3:4"];
const ACCENT = 'var(--color-accent-primary)';
// 主题变量无法拼 hex-alpha，统一用 color-mix 生成浅色底
const accentBg = (color, pct = 12) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

const STORYBOARD_DRAFT_DEFAULTS = {
  draftStatus: "rough",
  content: "",
  storyPurpose: "",
  shotSize: "中景",
  cameraAngle: "平视",
  cameraMovement: "固定",
  composition: "",
  subjectPositions: "",
  axisType: "自由或静态",
  axisSide: "待定",
  screenDirection: "不适用",
  lookDirection: "",
  axisStatus: "需复核",
  axisReason: "",
  informationControl: "",
  sound: "",
  continuity: "",
  notes: "",
};
const STORYBOARD_DRAFT_KEYS = Object.keys(STORYBOARD_DRAFT_DEFAULTS);
const SHOT_SIZE_OPTIONS = ["大全景", "全景", "中景", "近景", "特写"];
const CAMERA_ANGLE_OPTIONS = ["平视", "俯拍", "仰拍", "侧面", "过肩", "主观"];
const CAMERA_MOVEMENT_OPTIONS = ["固定", "推", "拉", "摇", "移", "跟", "环绕"];
const AXIS_TYPE_OPTIONS = ["I形轴线", "L形轴线", "自由或静态"];
const SCREEN_DIRECTION_OPTIONS = ["左→右", "右→左", "向镜头", "远离镜头", "静止", "不适用"];
const AXIS_STATUS_OPTIONS = ["稳定", "有意越轴", "需复核"];
const STORYBOARD_ADJUSTMENT_MARKER = "\n\n【人工故事板调整】\n";

const readStoryboardDraft = (data) => Object.fromEntries(
  STORYBOARD_DRAFT_KEYS.map((key) => [key, data[key] ?? STORYBOARD_DRAFT_DEFAULTS[key]])
);

const buildStoryboardAdjustmentPrompt = (draft) => {
  const shotLanguage = [draft.shotSize, draft.cameraAngle, draft.cameraMovement]
    .filter(Boolean)
    .join("，");
  return [
    draft.content && `画面：${draft.content}`,
    draft.storyPurpose && `叙事目的：${draft.storyPurpose}`,
    shotLanguage && `镜头：${shotLanguage}`,
    draft.composition && `构图：${draft.composition}`,
    draft.subjectPositions && `人物与主体位置：${draft.subjectPositions}`,
    draft.screenDirection && draft.screenDirection !== "不适用" && `屏幕动势：${draft.screenDirection}`,
    draft.lookDirection && `视线方向：${draft.lookDirection}`,
    draft.continuity && `连续性：${draft.continuity}`,
  ].filter(Boolean).join("\n");
};

export default function AtomEditPanel({ data, id, kind, onClose, initialView = "generate" }) {
  const isVideo = kind === "video";
  const isText = kind === "text";
  const legacyType = data.legacyType || "";
  const isMotion = legacyType === "motionStoryboard";
  const isGenerateNode = legacyType === "generate";
  const isStoryboardShot = legacyType === "storyboardShot";
  const textareaRef = useRef(null);
  const [prompt, setPrompt] = useState(
    data.rawPrompt ?? (data.prompt || data.imagePrompt || data.videoPrompt || ""),
  );
  const initialStoryboardDraft = readStoryboardDraft(data);
  const [storyboardDraft, setStoryboardDraft] = useState(initialStoryboardDraft);
  // Seedance 线路由项目固定，避免国内、EngGuaVideo 与 Omini 在节点缓存中串线。
  const [provider, setProvider] = useState(() => {
    const rawProjectDefault = getProjectVideoProvider(data.projectId);
    const projectDefault = normalizeSeedance25SubmissionProvider(rawProjectDefault) || rawProjectDefault;
    const saved = normalizeSeedance25SubmissionProvider(data.videoProvider) || data.videoProvider;
    // 国内/出海线路按项目市场互斥：持久化的线路与项目市场不符时回退到项目默认
    if (isSeedanceVideoProvider(saved) && !isDomesticOfficialSeedance(saved) && saved !== projectDefault) return projectDefault;
    return saved || projectDefault;
  });
  const [subModel, setSubModel] = useState(data.videoModel || "");
  const [duration, setDuration] = useState(data.videoDuration || data.duration || 5);
  const projectDefaultVideoRatio = getProjectDefaultVideoRatio(
    data.projectId || localStorage.getItem("currentProjectId"),
  );
  const [aspectRatio, setAspectRatio] = useState(
    data.aspectRatio || (isWanxVideoProvider(provider) ? "adaptive" : projectDefaultVideoRatio),
  );
  const [videoResolution, setVideoResolution] = useState(() => (
    normalizeCanvasVideoResolution(provider, data.videoResolution)
  ));
  const [imageProvider, setImageProvider] = useState(() => (
    normalizeImageProvider(data.imageProvider || getCurrentImageProvider())
  ));
  // 图片画质档位：1K/2K/4K（imageQualityTiers.js），默认中间档 2K
  const [imageQualityTier, setImageQualityTier] = useState(() => (
    normalizeWanxImageQualityTier(data.imageQualityTier || '2K', imageProvider)
  ));
  // 普通图片节点默认携带项目画风；旧节点缺少字段时也保持开启。
  const [includeProjectStyle, setIncludeProjectStyle] = useState(data.includeProjectStyle !== false);
  // GuaVideo 高级参数（默认值与 useCanvasGeneration 内部默认一致，不改变既有生成行为）
  const [guaVideoMode, setGuaVideoMode] = useState(data.guaVideoMode || "ref2v");
  const [seedance25Mode, setSeedance25Mode] = useState(() => (
    data.seedance25Mode || data.generationMode || data.videoGenerationMode
      || ({ ref2v: "reference", i2v: "img2video", t2v: "text2video" }[data.guaVideoMode]) || "reference"
  ));
  const [guaAudio, setGuaAudio] = useState(data.guaAudio ?? true);
  const [guaWebSearch, setGuaWebSearch] = useState(data.guaWebSearch ?? false);
  const [wanxMode, setWanxMode] = useState(data.wanxMode || "img2video");
  const [wanxAudio, setWanxAudio] = useState(data.wanxAudio ?? true);
  const [wanxWatermark, setWanxWatermark] = useState(data.wanxWatermark ?? false);
  const [wanxSeed, setWanxSeed] = useState(data.wanxSeed ?? "");
  const [description, setDescription] = useState(data.description || "");
  const [text, setText] = useState(data.text || data.description || "");
  const [selectedLighting, setSelectedLighting] = useState(data.lighting || null);
  const [settingsOpen, setSettingsOpen] = useState(false); // 生成设置弹框
  const normalizedInitialView = initialView === "history" ? "history" : "generate";
  const [activeView, setActiveView] = useState(normalizedInitialView);

  useEffect(() => {
    if (isGenerateNode) setActiveView(normalizedInitialView);
  }, [isGenerateNode, normalizedInitialView]);

  useEffect(() => {
    const syncImageProvider = () => setImageProvider(getCurrentImageProvider());
    window.addEventListener("storage", syncImageProvider);
    window.addEventListener(IMAGE_PROVIDER_CHANGE_EVENT, syncImageProvider);
    return () => {
      window.removeEventListener("storage", syncImageProvider);
      window.removeEventListener(IMAGE_PROVIDER_CHANGE_EVENT, syncImageProvider);
    };
  }, []);

  useEffect(() => {
    setImageQualityTier((current) => normalizeWanxImageQualityTier(current, imageProvider));
  }, [imageProvider]);

  const autoResize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  const externalStoryboardDraftSignature = JSON.stringify(readStoryboardDraft(data));
  const externalEditSignature = JSON.stringify({
    prompt: data.prompt,
    rawPrompt: data.rawPrompt,
    imagePrompt: data.imagePrompt,
    videoPrompt: data.videoPrompt,
    lighting: data.lighting,
    description: data.description,
    text: data.text,
    includeProjectStyle: data.includeProjectStyle,
    storyboardDraft: externalStoryboardDraftSignature,
  });
  const lastExternalEditSignatureRef = useRef(externalEditSignature);

  // 节点被任务轮询、撤销/重做或外部面板更新时，同步本地编辑态。
  // 不在 render 期间 setState，避免大画布中产生重复渲染和更新深度异常。
  useEffect(() => {
    if (externalEditSignature === lastExternalEditSignatureRef.current) return undefined;
    lastExternalEditSignatureRef.current = externalEditSignature;
    const nextStoryboardDraft = JSON.parse(externalStoryboardDraftSignature);
    const frameId = window.requestAnimationFrame(() => {
      setPrompt(data.rawPrompt ?? (data.prompt || data.imagePrompt || data.videoPrompt || ""));
      setDescription(data.description || "");
      setText(data.text || data.description || "");
      setIncludeProjectStyle(data.includeProjectStyle !== false);
      setSelectedLighting(data.lighting || null);
      setStoryboardDraft(nextStoryboardDraft);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [
    data.description,
    data.imagePrompt,
    data.includeProjectStyle,
    data.lighting,
    data.prompt,
    data.rawPrompt,
    data.text,
    data.videoPrompt,
    externalEditSignature,
    externalStoryboardDraftSignature,
  ]);

  useEffect(() => {
    autoResize(textareaRef.current);
  }, [activeView, prompt, text]);

  const providerConfig = getProviderConfig(provider);
  const subModels = providerConfig?.subModels || [];
  const currentSubModel = subModels.find(m => m.id === subModel) || subModels[0];
  const mc = VIDEO_PROVIDERS.find(p => p.id === provider) || (getSeedance25Capabilities(provider)?.retired
    ? { id: provider, name: `${getSeedance25Capabilities(provider).label} · 已停用`, color: 'var(--color-warning)' }
    : VIDEO_PROVIDERS[0]);

  const handleProviderChange = useCallback((newProvider) => {
    setProvider(newProvider);
    const cfg = getProviderConfig(newProvider);
    const seedance25Capabilities = getSeedance25Capabilities(newProvider);
    if (seedance25Capabilities) {
      setSeedance25Mode((current) => seedance25Capabilities.modes.includes(current) ? current : seedance25Capabilities.modes[0]);
    }
    const subs = cfg?.subModels || [];
    if (subs.length > 0) {
      setSubModel(subs[0].id);
      setDuration(subs[0].defaultDuration || 5);
    }
    if (isWanxVideoProvider(newProvider)) {
      setSubModel(WANX_VIDEO_MODEL);
      setDuration(5);
      setAspectRatio("adaptive");
      setVideoResolution("1080P");
    } else {
      setAspectRatio("16:9");
      setVideoResolution(normalizeVideoResolutionForProvider(newProvider, "720p", "720p"));
    }
  }, []);

  const handleSubModelChange = useCallback((smId) => {
    const sm = (getProviderConfig(provider)?.subModels || []).find(m => m.id === smId);
    setSubModel(smId);
    setDuration(sm?.defaultDuration || 5);
  }, [provider]);

  const buildPayload = () => ({
    ...data,
    prompt: applyLighting(prompt, selectedLighting),
    ...(isGenerateNode ? { rawPrompt: prompt } : {}),
    // storyboardShot 生图链路读 imagePrompt：编辑后同步回写，保证主镜/连接镜节点改提示词后生图生效
    ...(isStoryboardShot ? {
      ...storyboardDraft,
      imagePrompt: applyLighting(prompt, selectedLighting),
    } : {}),
    aspectRatio: isVideo && isWanxVideoProvider(provider)
      ? normalizeWanxVideoRatio(aspectRatio)
      : isVideo ? aspectRatio : normalizeDatastartImageAspectRatio(aspectRatio, imageProvider),
    description: isText ? text : description,
    // 文本节点：正文持久化到 data.text
    ...(isText ? { text } : {}),
    lighting: selectedLighting,
    ...(isVideo ? {
      videoProvider: provider,
      videoModel: isWanxVideoProvider(provider)
        ? normalizeWanxVideoModel(subModel || currentSubModel?.id)
        : (subModel || currentSubModel?.id),
      videoDuration: duration,
      videoResolution: normalizeCanvasVideoResolution(provider, videoResolution),
      ...(getSeedance25Capabilities(provider) ? { seedance25Mode } : {}),
      guaVideoMode,
      guaAudio,
      guaWebSearch,
      wanxMode,
      wanxAudio,
      wanxWatermark,
      wanxSeed,
    } : {}),
    // 图片节点：画质档位（imageQualityTiers 中间档默认 2K）
    ...(!isVideo && !isText ? {
      imageProvider,
      imageQualityTier: normalizeWanxImageQualityTier(imageQualityTier, imageProvider),
      ...(isGenerateNode ? { includeProjectStyle } : {}),
    } : {}),
  });

  const syncAndGenerate = () => {
    const payload = buildPayload();
    data.onUpdate?.(id, payload);
    data.onGenerate?.(id, payload);
  };

  const syncOnBlur = () => {
    data.onUpdate?.(id, buildPayload());
  };

  const persistStoryboardDraft = (nextDraft, nextPrompt = prompt) => {
    const promptWithLighting = applyLighting(nextPrompt, selectedLighting);
    data.onUpdate?.(id, {
      ...buildPayload(),
      ...nextDraft,
      prompt: promptWithLighting,
      imagePrompt: promptWithLighting,
    });
  };

  const handleDraftStatusChange = (draftStatus) => {
    const nextDraft = { ...storyboardDraft, draftStatus };
    setStoryboardDraft(nextDraft);
    persistStoryboardDraft(nextDraft);
  };

  const applyStoryboardDraftToPrompt = () => {
    const adjustment = buildStoryboardAdjustmentPrompt(storyboardDraft);
    if (!adjustment) return;
    const basePrompt = String(prompt || "").split(STORYBOARD_ADJUSTMENT_MARKER)[0].trim();
    const nextPrompt = `${basePrompt}${basePrompt ? STORYBOARD_ADJUSTMENT_MARKER : "【人工故事板调整】\n"}${adjustment}`;
    setPrompt(nextPrompt);
    persistStoryboardDraft(storyboardDraft, nextPrompt);
  };

  const isGenerating = data.isGenerating;
  const accentColor = isVideo ? (mc.color || ACCENT) : ACCENT;
  const generationHistory = isGenerateNode ? normalizeGenerationHistory(data) : [];
  const currentGenerationEntry = isGenerateNode
    ? getCurrentGenerationHistoryEntry(data)
    : null;
  const lastResultUrl = currentGenerationEntry?.url || data.lastResultUrl || null;
  const isFailed = data.generatingStatus === 'failed';
  const isInterrupted = data.generatingStatus === 'interrupted';
  const hasRecoverableTask = isInterrupted && Boolean(data.activeTaskId && data.activeTaskKind);
  // 设置按钮摘要：如 "GuaVideo · 720p · 5s"
  const settingsSummary = `${mc.name} · ${videoResolution} · ${Number(duration) === -1 ? "智能时长" : `${duration}s`}`;
  const referenceImageCount = Array.isArray(data.connectedImages)
    ? data.connectedImages.length
    : Array.isArray(data.referenceImages)
      ? data.referenceImages.length
      : 0;
  const generationCostRequest = isText ? null : {
    type: isVideo ? "video" : "image",
    model: isVideo ? provider : imageProvider,
    subModel: isVideo
      ? (isWanxVideoProvider(provider)
        ? normalizeWanxVideoModel(subModel || currentSubModel?.id)
        : (subModel || currentSubModel?.id || ""))
      : "",
    resolution: isVideo
      ? normalizeCanvasVideoResolution(provider, videoResolution)
      : normalizeWanxImageQualityTier(imageQualityTier, imageProvider),
    durationSeconds: isVideo
      ? (Number(duration) === -1 ? -1 : Math.max(2, Number(duration) || 5))
      : undefined,
    count: 1,
    promptCharacters: String(prompt || "").length,
    referenceImageCount,
    source: "canvas_mode",
  };

  const handleSelectHistoryVersion = useCallback((entry) => {
    if (!entry?.id || data.isGenerating) return;
    data.onUpdate?.(id, (previousData) => ({
      ...previousData,
      ...selectGenerationHistoryVersion(previousData, entry.id),
    }));
  }, [data.isGenerating, data.onUpdate, id]);

  const handleRestoreHistoryParameters = useCallback((entry) => {
    if (!entry?.id || data.isGenerating) return;
    const patch = restoreGenerationHistoryParameters(data, entry.id);
    if (Object.keys(patch).length === 0) return;

    if (patch.prompt !== undefined) setPrompt(patch.prompt);
    if (Object.prototype.hasOwnProperty.call(patch, "lighting")) {
      setSelectedLighting(patch.lighting || null);
    }
    if (patch.aspectRatio) setAspectRatio(patch.aspectRatio);
    if (patch.imageProvider) setImageProvider(normalizeImageProvider(patch.imageProvider));
    if (patch.imageQualityTier) {
      setImageQualityTier(normalizeWanxImageQualityTier(
        patch.imageQualityTier,
        patch.imageProvider || imageProvider,
      ));
    }
    if (patch.includeProjectStyle !== undefined) setIncludeProjectStyle(patch.includeProjectStyle);
    if (patch.videoProvider) setProvider(patch.videoProvider);
    if (patch.videoModel) setSubModel(patch.videoModel);
    if (patch.videoDuration !== undefined) setDuration(patch.videoDuration);
    if (patch.videoResolution) {
      setVideoResolution(normalizeCanvasVideoResolution(
        patch.videoProvider || provider,
        patch.videoResolution,
      ));
    }
    if (patch.guaVideoMode) setGuaVideoMode(patch.guaVideoMode);
    if (patch.seedance25Mode) setSeedance25Mode(patch.seedance25Mode);
    if (patch.guaAudio !== undefined) setGuaAudio(patch.guaAudio);
    if (patch.guaWebSearch !== undefined) setGuaWebSearch(patch.guaWebSearch);
    if (patch.wanxMode) setWanxMode(patch.wanxMode);
    if (patch.wanxAudio !== undefined) setWanxAudio(patch.wanxAudio);
    if (patch.wanxWatermark !== undefined) setWanxWatermark(patch.wanxWatermark);
    if (patch.wanxSeed !== undefined) setWanxSeed(patch.wanxSeed ?? "");

    data.onUpdate?.(id, (previousData) => ({
      ...previousData,
      ...restoreGenerationHistoryParameters(previousData, entry.id),
    }));
    setActiveView("generate");
  }, [data, id, imageProvider, provider]);

  return (
    <div
      className="nodrag nowheel rounded-lg"
      style={{
        background: "var(--color-bg-secondary)",
        border: "1px solid var(--color-border-primary)",
        overflow: "hidden",
        marginTop: 6,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {isGenerateNode && (
        <div
          className="flex items-center gap-1 border-b px-3 pt-1"
          style={{ borderColor: "var(--color-border-secondary)" }}
          role="tablist"
          aria-label="生成节点页面"
        >
          {[
            { id: "generate", label: "生成", Icon: Wand2 },
            { id: "history", label: "历史", Icon: History, count: generationHistory.length },
          ].map(({ id: viewId, label, Icon, count }) => {
            const active = activeView === viewId;
            return (
              <button
                key={viewId}
                type="button"
                role="tab"
                aria-selected={active}
                className="nodrag nopan flex h-9 min-w-24 items-center justify-center gap-1.5 border-b-2 px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-primary)]"
                style={{
                  color: active ? ACCENT : "var(--color-text-muted)",
                  background: active ? accentBg(ACCENT, 7) : "transparent",
                  borderBottomColor: active ? ACCENT : "transparent",
                }}
                onClick={() => {
                  setActiveView(viewId);
                  if (viewId === "history") setSettingsOpen(false);
                }}
              >
                <Icon size={12} aria-hidden="true" />
                {label}
                {count !== undefined && (
                  <span
                    className="rounded-full px-1.5 py-0.5 text-2xs font-mono-num"
                    style={{
                      color: active ? ACCENT : "var(--color-text-muted)",
                      background: active ? accentBg(ACCENT, 13) : "var(--color-bg-tertiary)",
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* P2-1 generate 节点：最近结果缩略图（点击预览）+ 状态/失败原因 */}
      {isGenerateNode && activeView === "generate" && (lastResultUrl || isFailed || isInterrupted || isGenerating) && (
        <div className="flex items-center gap-2 px-3 pt-2.5">
          {lastResultUrl && (
            <button
              onClick={() => data.onImageClick?.(
                lastResultUrl,
                data.prompt || '生成结果',
                isVideo ? 'video' : 'image',
              )}
              className="w-11 h-11 rounded-md overflow-hidden shrink-0 transition-transform hover:scale-[1.03]"
              style={{ border: "1px solid var(--color-border-primary)" }}
              title="点击预览最近结果"
            >
              {isVideo ? (
                <video src={lastResultUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
              ) : (
                <img src={lastResultUrl} alt="最近结果" className="w-full h-full object-cover" />
              )}
            </button>
          )}
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="text-label">
              {isGenerating
                ? '生成中'
                : isFailed
                  ? '上次生成失败'
                  : isInterrupted
                    ? (hasRecoverableTask ? '任务查询已暂停' : '上次任务已中断')
                    : '最近结果'}
            </span>
            {isGenerating && (
              <span className="text-2xs flex items-center gap-1 truncate" style={{ color: 'var(--color-text-muted)' }}>
                <Loader2 size={10} className="animate-spin shrink-0" />
                {typeof data.generatingStatus === 'string' && data.generatingStatus !== 'failed' && data.generatingStatus !== 'completed'
                  ? data.generatingStatus
                  : '生成中，请稍候...'}
              </span>
            )}
            {isFailed && data.errorMessage && (
              <span className="text-2xs flex items-center gap-1 truncate" style={{ color: 'var(--color-error)' }} title={data.errorMessage}>
                <AlertCircle size={10} className="shrink-0" />
                {data.errorMessage}
              </span>
            )}
            {isInterrupted && (
              <span
                className="text-2xs flex items-center gap-1 truncate"
                style={{ color: hasRecoverableTask ? 'var(--color-warning)' : 'var(--color-text-muted)' }}
                title={data.errorMessage || (hasRecoverableTask ? '点击继续查询原任务，不会重复提交' : '请重新生成')}
              >
                <AlertCircle size={10} className="shrink-0" />
                {hasRecoverableTask ? '可继续查询原任务，不会重复提交' : (data.errorMessage || '请重新生成')}
              </span>
            )}
            {data.lastGeneratedAt && !isFailed && !isInterrupted && !isGenerating && (
              <span className="text-2xs font-mono-num" style={{ color: 'var(--color-text-muted)' }}>
                {new Date(data.lastGeneratedAt).toLocaleTimeString()}
              </span>
            )}
            {/* P3-1：结果已自动入资源中心 */}
            {data.resourceId && !isGenerating && (
              <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>已入资源中心</span>
            )}
          </div>
          {/* P2-1 就地更新：按需导出最近结果为独立素材节点（spawn 内幂等查重，重复点击不重复产出） */}
          {lastResultUrl && !isGenerating && (
            <button
              onClick={() => data.onExportResult?.(id)}
              className="flex items-center gap-1 px-2 h-6 rounded-md text-2xs font-medium shrink-0 transition-colors"
              style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)", border: "1px solid var(--color-border-secondary)" }}
              title="将最近结果产出为独立素材节点（可连线复用）"
            >
              <Share2 size={10} style={{ color: ACCENT }} />
              导出为素材节点
            </button>
          )}
        </div>
      )}

      {/* 历史与生成表单互斥显示，避免两个工作区挤在一起。 */}
      {isGenerateNode && activeView === "history" && (
        <div className="p-3">
          <GenerationHistoryStrip
            history={generationHistory}
            currentUrl={lastResultUrl || ""}
            currentId={data.currentGenerationHistoryId || currentGenerationEntry?.id || ""}
            mediaType={isVideo ? "video" : "image"}
            disabled={isGenerating}
            showHeader={false}
            onPreview={(entry, url) => data.onImageClick?.(
              url,
              entry?.prompt || (isVideo ? "历史视频" : "历史图片"),
              entry?.mediaType || (isVideo ? "video" : "image"),
            )}
            onSelect={handleSelectHistoryVersion}
            onRestoreParams={handleRestoreHistoryParameters}
          />
        </div>
      )}

      {/* 参考图预览条 */}
      {isVideo && (!isGenerateNode || activeView === 'generate') && <div className="px-3"><ModelArkReferenceLibraryPanel projectId={data.projectId} provider={provider} references={[...(data.connectedModelArkReferences || data.connectedImages || data.referenceImages || []), ...(data.guaRefVideos || []).map((item) => typeof item === 'string' ? { url: item, mediaType: 'Video' } : { ...item, mediaType: 'Video' })]} /></div>}
      {(!isGenerateNode || activeView === "generate") && data.connectedImages?.length > 0 && (
        <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
          <span className="text-label shrink-0">参考</span>
          <div className="flex gap-1.5 overflow-x-auto">
            {data.connectedImages.map((img, i) => (
              <div
                key={`${img.sourceNodeId || img.url}-${i}`}
                className="group/reference relative w-9 h-9 rounded-md overflow-hidden shrink-0"
                style={{ border: "1px solid var(--color-border-primary)" }}
                title={img.name || "参考图"}
              >
                <img src={img.url} alt={img.name || ''} className="w-full h-full object-cover" />
                <button
                  type="button"
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center opacity-0 group-hover/reference:opacity-100 focus-visible:opacity-100 transition-opacity"
                  style={{
                    color: "#fff",
                    background: "rgba(0, 0, 0, 0.72)",
                    border: "1px solid rgba(255, 255, 255, 0.32)",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    data.onRemoveReference?.(id, img);
                  }}
                  aria-label={`取消参考${img.name ? `：${img.name}` : ""}`}
                  title="取消参考并删除连线"
                >
                  <X size={10} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isStoryboardShot && (
        <div className="px-3 pt-2.5">
          <div
            className="rounded-md p-2.5"
            style={{
              background: "var(--color-bg-primary)",
              border: `1px solid ${storyboardDraft.axisStatus === "需复核" ? "var(--color-warning)" : "var(--color-border-primary)"}`,
            }}
          >
            <div className="flex items-center justify-between gap-2 mb-2">
              <div>
                <div className="text-xs font-semibold" style={{ color: "var(--color-text-primary)" }}>故事板粗稿</div>
                <div className="text-2xs mt-0.5" style={{ color: "var(--color-text-muted)" }}>AI 只提供起稿，逐项调整后再生图</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {[
                  { value: "rough", label: "粗稿" },
                  { value: "reviewed", label: "已复核" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleDraftStatusChange(option.value)}
                    className="h-6 px-2 rounded text-2xs font-medium transition-colors"
                    style={{
                      background: storyboardDraft.draftStatus === option.value ? accentBg(ACCENT, 16) : "transparent",
                      color: storyboardDraft.draftStatus === option.value ? ACCENT : "var(--color-text-muted)",
                      border: `1px solid ${storyboardDraft.draftStatus === option.value ? ACCENT : "var(--color-border-secondary)"}`,
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>画面内容</label>
            <textarea
              value={storyboardDraft.content}
              onChange={(event) => setStoryboardDraft((prev) => ({ ...prev, content: event.target.value }))}
              onBlur={syncOnBlur}
              rows={3}
              className="w-full rounded-md px-2 py-1.5 text-xs resize-y outline-none"
              style={{
                color: "var(--color-text-primary)",
                background: "var(--color-bg-secondary)",
                border: "1px solid var(--color-border-secondary)",
                minHeight: 62,
              }}
              placeholder="这一镜实际拍到什么"
            />

            <label className="block text-2xs mt-2 mb-1" style={{ color: "var(--color-text-muted)" }}>叙事目的</label>
            <input
              value={storyboardDraft.storyPurpose}
              onChange={(event) => setStoryboardDraft((prev) => ({ ...prev, storyPurpose: event.target.value }))}
              onBlur={syncOnBlur}
              className="w-full h-8 rounded-md px-2 text-xs outline-none"
              style={{
                color: "var(--color-text-primary)",
                background: "var(--color-bg-secondary)",
                border: "1px solid var(--color-border-secondary)",
              }}
              placeholder="这一镜必须让观众知道什么"
            />

            <div className="grid grid-cols-2 gap-1.5 mt-2">
              {[
                { key: "shotSize", label: "景别", options: SHOT_SIZE_OPTIONS },
                { key: "cameraAngle", label: "机位", options: CAMERA_ANGLE_OPTIONS },
                { key: "cameraMovement", label: "运镜", options: CAMERA_MOVEMENT_OPTIONS },
                { key: "screenDirection", label: "动势", options: SCREEN_DIRECTION_OPTIONS },
              ].map((field) => (
                <label key={field.key} className="min-w-0">
                  <span className="block text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>{field.label}</span>
                  <AppSelect
                    value={storyboardDraft[field.key]}
                    onChange={(event) => setStoryboardDraft((prev) => ({ ...prev, [field.key]: event.target.value }))}
                    onBlur={syncOnBlur}
                    className="w-full h-8 rounded-md px-2 text-xs outline-none"
                    style={{
                      color: "var(--color-text-primary)",
                      background: "var(--color-bg-secondary)",
                      border: "1px solid var(--color-border-secondary)",
                    }}
                  >
                    {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                  </AppSelect>
                </label>
              ))}
            </div>

            <details className="mt-2">
              <summary className="cursor-pointer text-2xs select-none" style={{ color: ACCENT }}>轴向、构图与连续性</summary>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {[
                  { key: "axisType", label: "轴线类型", options: AXIS_TYPE_OPTIONS },
                  { key: "axisStatus", label: "轴线状态", options: AXIS_STATUS_OPTIONS },
                ].map((field) => (
                  <label key={field.key} className="min-w-0">
                    <span className="block text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>{field.label}</span>
                    <AppSelect
                      value={storyboardDraft[field.key]}
                      onChange={(event) => setStoryboardDraft((prev) => ({ ...prev, [field.key]: event.target.value }))}
                      onBlur={syncOnBlur}
                      className="w-full h-8 rounded-md px-2 text-xs outline-none"
                      style={{
                        color: "var(--color-text-primary)",
                        background: "var(--color-bg-secondary)",
                        border: "1px solid var(--color-border-secondary)",
                      }}
                    >
                      {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                    </AppSelect>
                  </label>
                ))}
              </div>
              {[
                ["composition", "构图", "主体与前中后景关系"],
                ["subjectPositions", "人物左右", "人物或关键物体在画面中的位置"],
                ["axisSide", "机位侧", "摄影机位于关系轴哪一侧"],
                ["lookDirection", "视线", "人物看向哪里"],
                ["axisReason", "轴向说明", "越轴原因或需要人工复核的疑点"],
                ["informationControl", "信息控制", "本镜新增、隐藏或强调什么"],
                ["sound", "声音", "对白、环境声或音效"],
                ["continuity", "连续性", "与前后镜保持的动作、视线、光线或位置"],
                ["notes", "调整备注", "留给摄影、分镜或后续自己的备注"],
              ].map(([key, label, placeholder]) => (
                <label key={key} className="block mt-1.5">
                  <span className="block text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>{label}</span>
                  <textarea
                    value={storyboardDraft[key]}
                    onChange={(event) => setStoryboardDraft((prev) => ({ ...prev, [key]: event.target.value }))}
                    onBlur={syncOnBlur}
                    rows={2}
                    className="w-full rounded-md px-2 py-1.5 text-xs resize-y outline-none"
                    style={{
                      color: "var(--color-text-primary)",
                      background: "var(--color-bg-secondary)",
                      border: "1px solid var(--color-border-secondary)",
                      minHeight: 46,
                    }}
                    placeholder={placeholder}
                  />
                </label>
              ))}
            </details>

            <button
              type="button"
              onClick={applyStoryboardDraftToPrompt}
              className="mt-2 w-full h-8 rounded-md flex items-center justify-center gap-1.5 text-2xs font-medium transition-colors"
              style={{
                color: ACCENT,
                background: accentBg(ACCENT, 12),
                border: `1px solid ${accentBg(ACCENT, 35)}`,
              }}
              title="把当前画面、机位、构图、人物位置和动势写入下方生图提示词"
            >
              <Pencil size={11} />
              应用人工调整到生图提示词
            </button>
          </div>
        </div>
      )}

      {/* 输入框 */}
      {(!isGenerateNode || activeView === "generate") && (
        <div className="px-3 pt-2 pb-1">
          {isStoryboardShot && (
            <div className="text-2xs mb-1" style={{ color: "var(--color-text-muted)" }}>生图提示词</div>
          )}
          <textarea
            ref={textareaRef}
            value={isText ? text : prompt}
            onChange={(e) => {
              isText ? setText(e.target.value) : setPrompt(e.target.value);
              autoResize(e.target);
            }}
            onBlur={syncOnBlur}
            className="w-full text-xs bg-transparent resize-none outline-none leading-relaxed"
            style={{ color: "var(--color-text-primary)", minHeight: 72, maxHeight: 200, overflow: "auto" }}
            placeholder={isText ? "输入文本内容..." : "描述你想生成的内容..."}
          />
        </div>
      )}

      {/* 底部操作栏 + 发送按钮 */}
      {!isText && (!isGenerateNode || activeView === "generate") && (
        <div className="flex items-center justify-between px-2.5 pb-2.5 pt-0.5">
          <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0">
            {isVideo ? (
              /* 视频节点：参数收进“生成设置”弹框，面板只留摘要按钮 */
              <button
                onClick={() => setSettingsOpen(true)}
                className="flex items-center gap-1.5 px-2.5 h-7 rounded-md text-2xs font-medium whitespace-nowrap transition-colors"
                style={{
                  background: "var(--color-bg-tertiary)",
                  color: "var(--color-text-secondary)",
                  border: "1px solid var(--color-border-secondary)",
                }}
                title="生成设置（模型 / 时长 / 画幅 / 分辨率 / 打光）"
              >
                <Settings2 size={11} style={{ color: accentColor }} />
                <span className="font-mono-num">{settingsSummary}</span>
                <ChevronDown size={9} style={{ color: "var(--color-text-muted)" }} />
              </button>
            ) : (
              /* 图片节点：参数少，保持面板内平铺（比例 + 画质） */
              <>
                <ImageProviderSelect
                  value={imageProvider}
                  onChange={setImageProvider}
                  className="h-7 max-w-[230px] py-1"
                />

                {getCompatibleImageAspectOptions(ASPECT_RATIOS.map(value => ({ value, label: value })), imageProvider).map(({ value: r, label }) => (
                  <button
                    key={r}
                    title={`画幅比例 ${label}`}
                    onClick={() => setAspectRatio(r)}
                    className="px-2 h-6 rounded-md text-2xs font-medium font-mono-num whitespace-nowrap transition-colors"
                    style={{
                      background: normalizeDatastartImageAspectRatio(aspectRatio, imageProvider) === r ? accentBg(accentColor) : "transparent",
                      color: normalizeDatastartImageAspectRatio(aspectRatio, imageProvider) === r ? accentColor : "var(--color-text-muted)",
                    }}
                  >
                    {r}
                  </button>
                ))}

                <div className="w-px h-3.5 mx-0.5" style={{ background: "var(--color-border-secondary)" }} />

                {/* 画质档位（imageQualityTiers.js，默认中间档 2K） */}
                <span className="text-2xs shrink-0" style={{ color: "var(--color-text-muted)" }}>画质</span>
                {getWanxCompatibleQualityOptions(IMAGE_QUALITY_TIER_OPTIONS, imageProvider).map(q => (
                  <button
                    key={q.value}
                    title={`生成画质 ${q.label}`}
                    onClick={() => setImageQualityTier(q.value)}
                    className="px-2 h-6 rounded-md text-2xs font-medium font-mono-num whitespace-nowrap transition-colors"
                    style={{
                      background: imageQualityTier === q.value ? accentBg(accentColor) : "transparent",
                      color: imageQualityTier === q.value ? accentColor : "var(--color-text-muted)",
                    }}
                  >
                    {q.label}
                  </button>
                ))}

                {isGenerateNode && (
                  <>
                    <div className="w-px h-3.5 mx-0.5" style={{ background: "var(--color-border-secondary)" }} />
                    <button
                      type="button"
                      aria-pressed={includeProjectStyle}
                      onClick={() => setIncludeProjectStyle(value => !value)}
                      className="flex items-center gap-1.5 px-2 h-6 rounded-md text-2xs font-medium whitespace-nowrap transition-colors"
                      style={{
                        background: includeProjectStyle ? accentBg(accentColor) : "transparent",
                        color: includeProjectStyle ? accentColor : "var(--color-text-muted)",
                      }}
                      title={includeProjectStyle ? "生成时携带项目画风，点击关闭" : "当前不携带项目画风，点击开启"}
                    >
                      <Palette size={11} />
                      项目画风
                      <span
                        className="relative inline-flex w-6 h-3.5 rounded-full transition-colors"
                        style={{
                          background: includeProjectStyle ? accentColor : "var(--color-bg-elevated)",
                          border: "1px solid var(--color-border-secondary)",
                        }}
                      >
                        <span
                          className="absolute top-0.5 w-2 h-2 rounded-full transition-transform"
                          style={{
                            background: includeProjectStyle ? "var(--color-accent-contrast)" : "var(--color-text-muted)",
                            transform: includeProjectStyle ? "translateX(12px)" : "translateX(2px)",
                          }}
                        />
                      </span>
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* 动态分镜编辑器按钮 */}
          {isMotion && (
            <button
              title="打开动态分镜编辑器"
              onClick={() => data.onOpenEditor?.(id)}
              className="flex items-center gap-1 px-2.5 h-6 rounded-md text-2xs font-medium shrink-0 ml-1 transition-colors"
              style={{ background: "var(--color-accent-light)", color: ACCENT }}
            >
              <Pencil size={11} />
              编辑器
            </button>
          )}

          <EstimatedPoints request={generationCostRequest} compact className="ml-1" />

          {/* P2-1 generate 节点：参数保留，可一键重新生成 */}
          {isGenerateNode && (
            <button
              onClick={syncAndGenerate}
              disabled={isGenerating}
              className="flex items-center gap-1 px-2.5 h-6 rounded-md text-2xs font-medium shrink-0 ml-1 transition-colors"
              style={{
                background: isGenerating ? "var(--color-bg-tertiary)" : accentBg(ACCENT, 15),
                color: isGenerating ? "var(--color-text-muted)" : ACCENT,
                cursor: isGenerating ? "not-allowed" : "pointer",
              }}
              title={hasRecoverableTask ? "继续查询原生成任务" : "使用当前参数重新生成"}
            >
              <RotateCcw size={11} />
              {hasRecoverableTask ? '继续查询' : '重新生成'}
            </button>
          )}

          {/* 发送按钮 */}
          <button
            onClick={syncAndGenerate}
            disabled={isGenerating}
            className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 ml-2 transition-colors"
            style={{
              background: isGenerating ? "var(--color-bg-tertiary)" : ACCENT,
              color: isGenerating ? "var(--color-text-muted)" : "var(--color-accent-contrast)",
              cursor: isGenerating ? "not-allowed" : "pointer",
            }}
            title={isGenerateNode ? (hasRecoverableTask ? "继续查询原生成任务" : "生成") : "发送"}
          >
            {isGenerating
              ? <Loader2 size={14} className="animate-spin" />
              : hasRecoverableTask
                ? <RotateCcw size={14} />
                : <Send size={14} />}
          </button>
        </div>
      )}

      {/* 文本节点只有关闭按钮 */}
      {isText && (
        <div className="flex justify-end px-3 pb-2">
          <button title="完成并收起面板" onClick={onClose} className="text-2xs px-2 h-6 rounded-md transition-colors" style={{ color: "var(--color-text-muted)", background: "var(--color-bg-tertiary)" }}>
            完成
          </button>
        </div>
      )}

      {/* 生成设置弹框（视频节点）：改动即时写回面板 state，生成参数与现状逐字一致 */}
      {isVideo && (!isGenerateNode || activeView === "generate") && (
        <GenerateSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          projectId={data.projectId}
          provider={provider}
          onProviderChange={handleProviderChange}
          subModel={subModel || currentSubModel?.id}
          onSubModelChange={handleSubModelChange}
          duration={duration}
          onDurationChange={setDuration}
          aspectRatio={aspectRatio}
          onAspectRatioChange={setAspectRatio}
          videoResolution={videoResolution}
          onVideoResolutionChange={setVideoResolution}
          guaVideoMode={guaVideoMode}
          seedance25Mode={seedance25Mode}
          onSeedance25ModeChange={setSeedance25Mode}
          onGuaVideoModeChange={setGuaVideoMode}
          guaAudio={guaAudio}
          onGuaAudioChange={setGuaAudio}
          guaWebSearch={guaWebSearch}
          onGuaWebSearchChange={setGuaWebSearch}
          wanxMode={wanxMode}
          onWanxModeChange={setWanxMode}
          wanxAudio={wanxAudio}
          onWanxAudioChange={setWanxAudio}
          wanxWatermark={wanxWatermark}
          onWanxWatermarkChange={setWanxWatermark}
          wanxSeed={wanxSeed}
          onWanxSeedChange={setWanxSeed}
          selectedLighting={selectedLighting}
          onLightingChange={setSelectedLighting}
        />
      )}
    </div>
  );
}
